import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Badges as Timesheet Hub renders them: small rounded rectangles (5px),
 * 12px semibold, sentence case, tinted background with darker text of the
 * same hue. Token pairs are AA per the GW contrast matrix: success 6.38:1,
 * error 7.02:1, info 7.44:1, warning uses the safe #8a5800.
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-[5px] border px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        success:
          "border-[var(--border-success)] bg-[var(--bg-success)] text-[var(--gw-text-success)]",
        error:
          "border-[var(--border-error)] bg-[var(--bg-error)] text-[var(--gw-text-error)]",
        info: "border-[var(--border-info)] bg-[var(--bg-info)] text-[var(--gw-text-info)]",
        warning:
          "border-[var(--border-warning)] bg-[var(--bg-warning)] text-[var(--gw-text-warning-safe)]",
        neutral: "border-border bg-muted text-muted-foreground",
        // Legacy aliases kept for compatibility; mapped onto the GW set.
        default:
          "border-[var(--border-success)] bg-[var(--bg-success)] text-[var(--gw-text-success)]",
        secondary: "border-[var(--border-info)] bg-[var(--bg-info)] text-[var(--gw-text-info)]",
        destructive:
          "border-[var(--border-error)] bg-[var(--bg-error)] text-[var(--gw-text-error)]",
        outline: "border-border bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
